import React, { CSSProperties, ReactNode, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { useGetClientsQuery, useAdminOfLibrariesQuery, useMoveContentsMutation } from '../../../generated/graphql';
import { FormAutoComplete } from '../../form';
import styles from './MoveContentsDialog.module.css';

type MoveContentsDialogProps = {
    onClose: () => void,
    libraryId: string,
    ids: string[],
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const MoveContentsDialog = (props: MoveContentsDialogProps) => {
    const { libraryId, ids, style, visible, onClose } = props;
    const [moveContents, moveContentsResult] = useMoveContentsMutation();
    const [searchClients, setSearchClients] = useState('');
    const [searchLibrary, setSearchLibrary] = useState('');
    const [validationError, setValidationError] = useState('');

    const clients = useGetClientsQuery({
        variables: { contains: searchClients }
    });

    const libraries = useAdminOfLibrariesQuery();

    const handleClientChanged = (value: string) => {
        setSearchClients(value);
        const client = clients.data?.ClientList?.nodes?.filter(l => l?.name === value)[0];
        if (client) libraries.refetch({ clientId: client.id });
    };

    const handleSubmit = () => {
        const selectedClient = clients.data?.ClientList?.nodes?.filter(c => c?.name === searchClients)[0];
        const selectedLibrary = libraries.data?.Me?.adminOfLibraries?.filter(l => l?.name === searchLibrary)[0];

        if (!searchClients) {
            setValidationError('Please fill all of the following fields.');
            return;
        }
        if (!selectedClient) {
            setValidationError('Please Select A Valid Client!');
            return;
        }
        if (!selectedLibrary) {
            setValidationError('Please Select A Valid Library!');
            return;
        }
        setValidationError('');
        moveContents({
            variables: {
                targetLibraryId: selectedLibrary?.id,
                libraryId,
                ids,
            }
        })
            .then(() => { onClose(); })
            .catch(() => {});
    };

    return (
        <DialogBase
            title="Move Contents"
            type="OkCancel"
            visible={visible}
            isLoading={moveContentsResult.loading || clients.loading || libraries.loading}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.MoveContentsDialog}
            style={style}
            data-testid="MoveContentsDialog">
            <FormAutoComplete
                label="Client"
                name="client"
                value={searchClients}
                onChange={handleClientChanged}
                options={(clients.data?.ClientList?.nodes?.map(c => c?.name) || []).filter((n): n is string => !!n)}
            />
            <FormAutoComplete
                label="Library"
                name="library"
                value={searchLibrary}
                onChange={setSearchLibrary}
                options={(libraries.data?.Me?.adminOfLibraries?.map(l => l?.name) || []).filter((n): n is string => !!n)}
            />
        </DialogBase>
    );
};

export default MoveContentsDialog;